import { TableCell } from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router";
import { Log, PaginatedListLog } from "../../../services/swagger";
import { LogService } from "../../../services/api";
import { useTranslation } from "react-i18next";
import DataView, { QueryParameters } from "../../organisms/DataView";
import ItemCard from "../../organisms/ItemCard";
import { useQuery } from "@tanstack/react-query";
import FormTemplate from "../../templates/FormTemplate";

export default function LogsSettingsPage() {
  const { t } = useTranslation("Logs");
  const navigate = useNavigate();
  const [query, setQuery] = useState<QueryParameters>();

  const loadLogs = async (): Promise<PaginatedListLog | null> => {
    if (!query) return null;
    const res = await LogService.getLogList({
      search: query.search,
      limit: query.limit,
      skip: query.skip,
      filters: query.filters,
      sortDirection: query.sortDirection,
      sortField: query.sortField,
    });
    if (res?.data?.data) return res.data.data;
    else return null;
  };

  const { data: logs, isPlaceholderData: isLoading } = useQuery({
    queryFn: loadLogs,
    queryKey: ["logs", { query }],
    refetchInterval: 1000 * 10,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    placeholderData: (previousData) => previousData,
    maxPages: 10,
  });

  const handleDataTableChange = (query: QueryParameters) => {
    setQuery(query);
  };

  const handleReferenceNavigation = (log: Log) => {
    if (!log?.object || !log?.model) return;
    if (log.model === "webhook")
      navigate(`/m/settings/integrations/webhooks/${log.object}`);
    else if (log.model === "api")
      navigate(`/m/settings/integrations/api/${log.object}`);
    else if (log.model === "company")
      navigate(`/m/business/companies/${log.object}`);
    else if (log.model === "product")
      navigate(`/m/business/products/${log.object}`);
    else if (log.model === "contact")
      navigate(`/m/business/contacts/${log.object}`);
    else navigate(`/m/${log.model}/${log.object}`);
  };

  return (
    <FormTemplate
      title={t("Logs")}
      disableFooter
      onCancel={() => navigate("/m/settings")}
    >
      <DataView
        name={t("Logs")}
        loading={isLoading || !logs}
        onItemClick={(item) => handleReferenceNavigation(item)}
        headers={[
          { field: "date", name: t("Date"), type: "date" },
          {
            field: "user",
            name: t("User"),
            type: "other",
            renderRowCell: (row: Log) => (
              <TableCell key={row._id}>
                {row.user?.username || row.username}
              </TableCell>
            ),
          },
          { field: "model", name: t("Model"), type: "string" },
          {
            field: "action",
            name: t("Action"),
            type: "other",
            renderRowCell: (row: Log) => (
              <TableCell key={row._id}>
                {row.action || `${row.object} ${(row as any).type}`}
              </TableCell>
            ),
          },
        ]}
        onChange={handleDataTableChange}
        items={logs?.docs || []}
        count={logs?.count || 0}
        renderItem={(log) => (
          <ItemCard log={log} onClick={() => handleReferenceNavigation(log)} />
        )}
        pagination
        queryFunction={LogService.getLogListQuery}
      />
    </FormTemplate>
  );
}
